import React from "react";
import { Alert, Button, Tag } from "antd";
import { StarFilled, EditOutlined } from "@ant-design/icons";
import {
  DIRECTION_LABELS,
  PLAN_TYPE_OPTIONS,
  PROJECT_TYPE_OPTIONS,
  STAIRCASE_OPTIONS,
  getRoomFields,
  isBhkProjectType,
} from "../../constants";

const Row = ({ label, value }) => (
  <div className="mmh-review-row">
    <span className="mmh-review-label">{label}</span>
    <span className="mmh-review-value">{value || <em>Not set</em>}</span>
  </div>
);

const Block = ({ title, onEdit, children }) => (
  <div className="mmh-review-block">
    <div className="mmh-review-block-head">
      <h4>{title}</h4>
      {onEdit && (
        <Button type="link" size="small" icon={<EditOutlined />} onClick={onEdit}>
          Edit
        </Button>
      )}
    </div>
    {children}
  </div>
);

const ImageDetail = ({ label, children }) => (
  <div className="mmh-review-image-detail">
    <span className="mmh-review-image-detail-label">{label}</span>
    <span className="mmh-review-image-detail-value">{children || <em>Not set</em>}</span>
  </div>
);

const labelOf = (options, value, key = "value") =>
  options.find((o) => String(o[key]) === String(value))?.label || "";

/**
 * Last stop before saving. The legacy flow had no equivalent — you found out
 * what you had saved by going back to the listing afterwards.
 */
const ReviewStep = ({ form, categoryName, customFields, imageItems, onGoToStep }) => {
  const filledRooms = getRoomFields(form.projectType).filter((f) => Number(form[f.name]) > 0);
  const filledCustomFields = customFields.filter((f) => f.name?.trim());
  const dpImage = imageItems.find((item) => item.isDp);
  const untaggedCount = imageItems.filter((item) => !item.keywords?.length).length;

  return (
    <section className="mmh-section mmh-review">
      <Alert
        type="success"
        showIcon
        className="mmh-step-alert"
        message="Ready to save"
        description="The product and all of its images are written together in a single step — nothing is saved until you press the button below."
      />

      <div className="mmh-review-grid">
        <Block title="Product Identity" onEdit={() => onGoToStep(0)}>
          <Row label="Product Name" value={form.projectName} />
          <Row label="Project Code" value={form.projectCode} />
          <Row label="Product Code" value={form.productCode} />
        </Block>

        <Block title="Dimensions & Structure" onEdit={() => onGoToStep(0)}>
          <Row label="Plot size" value={form.width && form.length ? `${form.width} x ${form.length} ft` : ""} />
          <Row label="Floors" value={form.floor} />
          <Row label="Plot area" value={form.plotArea ? `${form.plotArea} sq ft` : ""} />
          <Row label="Build-up area" value={form.buildUpArea ? `${form.buildUpArea} sq ft` : ""} />
          <Row label="Direction" value={DIRECTION_LABELS[form.direction] || form.direction} />
          <Row label="Staircase" value={labelOf(STAIRCASE_OPTIONS, form.staircaseType)} />
          <Row label="Basement" value={form.basement} />
          <Row label="Vastu priority" value={form.vastuPriority} />
        </Block>

        <Block title="Location & Category" onEdit={() => onGoToStep(0)}>
          <Row label="Pincode" value={form.pincode} />
          <Row label="Client location" value={form.clientLocation} />
          <Row label="Category" value={categoryName} />
          <Row label="Category type" value={form.categoryType} />
        </Block>

        <Block title="Project type & rooms" onEdit={() => onGoToStep(0)}>
          <Row label="Project type" value={labelOf(PROJECT_TYPE_OPTIONS, form.projectType)} />
          <Row
            label={isBhkProjectType(form.projectType) ? "Units" : "Rooms"}
            value={
              filledRooms.length
                ? filledRooms.map((f) => `${f.label.replace(/^No\.? of /, "")}: ${form[f.name]}`).join(", ")
                : ""
            }
          />
          <Row label="Parking" value={Number(form.parking) ? "Yes" : "No"} />
          <Row
            label="Custom fields"
            value={
              filledCustomFields.length
                ? filledCustomFields.map((f) => `${f.name}: ${f.value || "-"}`).join(", ")
                : ""
            }
          />
        </Block>

        <Block title="Cost & Client" onEdit={() => onGoToStep(0)}>
          <Row label="Project cost" value={form.projectCost} />
          <Row label="Plan type" value={labelOf(PLAN_TYPE_OPTIONS, form.planType)} />
          <Row label="Client name" value={form.clientName} />
          <Row label="Link" value={form.addLink} />
        </Block>
      </div>

      <Block title={`Images (${imageItems.length})`} onEdit={() => onGoToStep(1)}>
        {untaggedCount > 0 && (
          <Alert
            type="warning"
            showIcon
            className="mmh-step-alert"
            message={`${untaggedCount} image${untaggedCount > 1 ? "s have" : " has"} no keywords. They will still save, but they won't surface in search.`}
          />
        )}
        <div className="mmh-review-images">
          {imageItems.map((item, index) => (
            <div className={`mmh-review-image ${item.isDp ? "is-dp" : ""}`} key={item.uid}>
              <div className="mmh-review-image-thumb">
                <img src={item.preview} alt={item.title} />
                <span className="mmh-image-index">{index + 1}</span>
                {item.isDp && (
                  <span className="mmh-image-dp-badge">
                    <StarFilled /> DP
                  </span>
                )}
              </div>
              <div className="mmh-review-image-meta">
                <strong>{item.title || "Untitled"}</strong>
                <ImageDetail label="Category">
                  {[item.category, item.subCategory].filter(Boolean).join(" / ")}
                </ImageDetail>
                {item.otherCategory && (
                  <ImageDetail label="Other category">{item.otherCategory}</ImageDetail>
                )}
                <ImageDetail label="Style">{item.style}</ImageDetail>
                <ImageDetail label="Keywords">
                  {item.keywords?.length > 0 && (
                    <span className="mmh-review-image-keywords">
                      {item.keywords.map((keyword) => (
                        <Tag key={keyword}>{keyword}</Tag>
                      ))}
                    </span>
                  )}
                </ImageDetail>
                {item.description && (
                  <ImageDetail label="Description">{item.description}</ImageDetail>
                )}
              </div>
            </div>
          ))}
        </div>
        {!dpImage && (
          <Alert type="error" showIcon className="mmh-step-alert" message="No display picture selected." />
        )}
      </Block>

      {form.description && (
        <Block title="Description" onEdit={() => onGoToStep(0)}>
          <p className="mmh-review-desc">{form.description}</p>
        </Block>
      )}
    </section>
  );
};

export default ReviewStep;
